/* The emoji picker.
 *
 * A CURATED SET, not the full Unicode table. A church network reaches for a
 * narrow band of them — prayer, thanks, encouragement — and three short rows
 * that fit without scrolling beat two thousand behind a search box nobody
 * types into. The system picker still has everything for anyone who wants it.
 *
 * No package: emoji are ordinary characters and every browser already draws
 * them. A picker library would add ~200kB to render what a <span> renders for
 * free.
 */
import { useEffect, useRef, useState } from 'react'
import { Smile } from 'lucide-react'

/** Grouped so the ones people actually reach for come first. */
export const EMOJI_GROUPS: Record<string, string[]> = {
  Faith: ['🙏', '✝️', '🕊️', '📖', '⛪', '😇', '🌟', '✨', '🛐', '💒', '🤍', '🕯️'],
  Feeling: ['❤️', '😊', '🥰', '😢', '😭', '🥺', '😔', '😌', '🤗', '😍', '🙂', '😂'],
  Support: ['🙌', '👏', '💪', '🤝', '👍', '🫂', '🎉', '🔥', '💯', '⭐', '🌈', '🌻'],
}

/** The 🙂 button, and the panel it opens. */
export function EmojiButton({
  onPick,
  label = 'Emoji',
}: {
  onPick: (emoji: string) => void
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  /* Close on an outside click or Escape. Without both, the panel is a trap on
     a phone, where there is no obvious place to click "off" it. */
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
        className="grid h-7 w-7 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover hover:text-ink"
      >
        <Smile className="h-[18px] w-[18px]" aria-hidden />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Choose an emoji"
          /* Opens UPWARD. The composer sits at the bottom of a thread, so a
             panel below it would fall off the screen. */
          className="absolute bottom-full left-0 z-50 mb-s2 w-[268px] rounded-md border border-line-2 bg-surface p-s2 shadow-2"
        >
          {Object.entries(EMOJI_GROUPS).map(([group, list]) => (
            <div key={group} className="mb-s2 last:mb-0">
              <div className="px-s1 pb-1 text-xs font-bold text-ink-2">
                {group}
              </div>
              <div className="flex flex-wrap">
                {list.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => {
                      onPick(e)
                      // Left open: picking two in a row is common, and
                      // reopening for each is the kind of thing that makes a
                      // picker annoying enough to stop using.
                    }}
                    aria-label={e}
                    className="grid h-8 w-8 place-items-center rounded text-xl leading-none transition-colors hover:bg-hover"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Insert at the cursor rather than appending.
 *
 *  Somebody who clicked back into the middle of a sentence means it there. The
 *  caret is left after what was inserted, so typing continues naturally.
 */
export function insertAtCursor(
  input: HTMLInputElement | HTMLTextAreaElement | null,
  value: string,
  emoji: string,
): { text: string; caret: number } {
  const start = input?.selectionStart ?? value.length
  const end = input?.selectionEnd ?? value.length
  return {
    text: value.slice(0, start) + emoji + value.slice(end),
    caret: start + emoji.length,
  }
}
